import { t } from '@/i18n';
import { z } from 'zod';

/// ////////////////////////////////////////////
/// featureを跨いで使用するenumを定義する
/// ////////////////////////////////////////////

// Project

export const sheetTypeEnum = z.enum(['RS', 'SS', 'KS']);
export const SheetTypeLabelModifier = {
  RS: t('review-sheet'),
  SS: t('segment-sheet'),
  KS: t('fund-sheet'),
} as const satisfies Record<z.infer<typeof sheetTypeEnum>, string>;

export const fundSheetTypeEnum = z.enum([
  'standard',
  'local-government',
  'investment',
]);

export const FundSheetTypeLabelModifier = {
  standard: t('fund-sheet'),
  'local-government': t('local-government-fund-sheet'),
  investment: t('investment-fund-sheet'),
} as const satisfies Record<z.infer<typeof fundSheetTypeEnum>, string>;

export const projectCategoryEnum = z.enum([
  'continuation',
  'new',
  'requesting',
]);
export const ProjectCategoryLabelModifier = {
  continuation: t('project-category-continuation'),
  new: t('project-category-new'),
  requesting: t('project-category-requesting'),
} as const satisfies Record<z.infer<typeof projectCategoryEnum>, string>;

export const implementationMethodEnum = z.enum([
  'direct',
  'subsidy',
  'burden',
  'grant',
  'contribution',
  'others',
]);
export const ImplementationMethodLabelModifier = {
  direct: t('implementation-method-direct'),
  subsidy: t('implementation-method-subsidy'),
  burden: t('implementation-method-burden'),
  grant: t('implementation-method-grant'),
  contribution: t('implementation-method-contribution'),
  others: t('implementation-method-others'),
} as const satisfies Record<z.infer<typeof implementationMethodEnum>, string>;

export const reviewTypeEnum = z.enum([
  'public-process',
  'document',
  'unnecessary',
]);
export const ReviewTypeLabelModifier = {
  'public-process': t('review-type-public-process'),
  document: t('review-type-document'),
  unnecessary: t('review-type-unnecessary'),
} as const satisfies Record<z.infer<typeof reviewTypeEnum>, string>;

// Analysis
export const analysisTargetEnum = z.enum(['expenditure', 'payment']);
export const AnalysisTargetLabelModifier = {
  expenditure: t('expenditure-item'),
  payment: t('analysis-payment-tab-label'),
} as const satisfies Record<z.infer<typeof analysisTargetEnum>, string>;

export const aggregationTypeEnum = z.enum([
  'finalized',
  'finalizedForInitialBudget',
  'finalizedForSupplementaryBudget',
  'finalizedForSupplementaryBudget_1',
  'finalizedForSupplementaryBudget_2',
  'finalizedForSupplementaryBudget_3',
  'finalizedForSupplementaryBudget_4',
  'finalizedForSupplementaryBudget_5',
  'finalizedForReserveBudget',
  'finalizedForCarriedForwardBudget',
  'payment',
]);
export const AggregationTypeLabelModifier = {
  finalized: t('total-finalized-amount'),
  finalizedForInitialBudget: t('initial-budget'),
  finalizedForSupplementaryBudget: t('supplementary-budget'),
  finalizedForSupplementaryBudget_1: t('supplementary1-budget'),
  finalizedForSupplementaryBudget_2: t('supplementary2-budget'),
  finalizedForSupplementaryBudget_3: t('supplementary3-budget'),
  finalizedForSupplementaryBudget_4: t('supplementary4-budget'),
  finalizedForSupplementaryBudget_5: t('supplementary5-budget'),
  finalizedForReserveBudget: t('reserve-budget'),
  finalizedForCarriedForwardBudget: t('carry-over-finalized-amount'),
  payment: t('amount-of-expenditures'),
} as const satisfies Record<z.infer<typeof aggregationTypeEnum>, string>;

export const aggregationTargetTypeEnum = z.enum([
  'total',
  'general',
  'special',
]);
export const AggregationTargetTypeLabelModifier = {
  total: t('total'),
  general: t('general-account'),
  special: t('special-account'),
} as const satisfies Record<z.infer<typeof aggregationTargetTypeEnum>, string>;

export const analysisAxisEnum = z.enum(['organization', 'year', 'policy']);
export const AnalysisAxisLabelModifier = {
  organization: t('organization-in-charge'),
  year: '',
  policy: t('policy'),
} as const satisfies Record<z.infer<typeof analysisAxisEnum>, string>;

// 基金シート分析の分析軸
// `total` は「合計のみ（軸なし）」を表す URL 上のセンチネル。
// フォーム値・API には載せず（form では null、API では除外）、URL の「軸なし」を
// 「フォールバックで organization に戻すべき初期表示」と区別するために使う。
// （RS 分析の analysisAxisEnum の `year` と同じ役割: RS-4424）
export const ksAnalysisAxisEnum = z.enum([
  'organization',
  'project',
  'fund-owner',
  'total',
]);
export const KsAnalysisAxisLabelModifier = {
  organization: t('organization-in-charge'),
  project: t('ks-axis-project'),
  'fund-owner': t('fund-owner'),
  total: '',
} as const satisfies Record<z.infer<typeof ksAnalysisAxisEnum>, string>;

// 基金シート分析の集計対象（BE: KsAnalysisAggregationTargetType と一致）
export const ksAggregationTargetTypeEnum = z.enum([
  // 収入
  'granted-amount-by-the-country',
  'investment-income',
  'national-equivalent-of-investment-income',
  'business-income',
  'national-equivalent-of-business-income',
  'other-income-amount',
  'total-income',
  // 支出
  'business-expense',
  'administrative-expense',
  'chore-expenses-of-fund-establishing',
  'personnel-expenses-of-fund-establishing',
  'total-expense',
  // 返納額
  'amount-returned-to-the-national-treasury',
  'amount-returned-to-the-organization',
  // 残高
  'fund-balance-at-the-end-of-the-year',
  'national-equivalent-of-the-balance',
  // 基金設置法人の事務人件費（当該基金からの支出を除く）
  'chore-expenses-not-from-this-fund',
  'personnel-expenses-not-from-this-fund',
  'total-expenses-not-from-this-fund',
]);
export const KsAggregationTargetTypeLabelModifier = {
  'granted-amount-by-the-country': t(
    'ks-aggregation-target-granted-amount-by-the-country',
  ),
  'investment-income': t('ks-aggregation-target-investment-income'),
  'national-equivalent-of-investment-income': t(
    'ks-aggregation-target-national-equivalent-of-investment-income',
  ),
  'business-income': t('ks-aggregation-target-business-income'),
  'national-equivalent-of-business-income': t(
    'ks-aggregation-target-national-equivalent-of-business-income',
  ),
  'other-income-amount': t('ks-aggregation-target-other-income-amount'),
  'total-income': t('ks-aggregation-target-total-income'),
  'business-expense': t('ks-aggregation-target-business-expense'),
  'administrative-expense': t('ks-aggregation-target-administrative-expense'),
  'chore-expenses-of-fund-establishing': t(
    'ks-aggregation-target-chore-expenses-of-fund-establishing',
  ),
  'personnel-expenses-of-fund-establishing': t(
    'ks-aggregation-target-personnel-expenses-of-fund-establishing',
  ),
  'total-expense': t('ks-aggregation-target-total-expense'),
  'amount-returned-to-the-national-treasury': t(
    'ks-aggregation-target-amount-returned-to-the-national-treasury',
  ),
  'amount-returned-to-the-organization': t(
    'ks-aggregation-target-amount-returned-to-the-organization',
  ),
  'fund-balance-at-the-end-of-the-year': t(
    'ks-aggregation-target-fund-balance-at-the-end-of-the-year',
  ),
  'national-equivalent-of-the-balance': t(
    'ks-aggregation-target-national-equivalent-of-the-balance',
  ),
  'chore-expenses-not-from-this-fund': t(
    'ks-aggregation-target-chore-expenses-not-from-this-fund',
  ),
  'personnel-expenses-not-from-this-fund': t(
    'ks-aggregation-target-personnel-expenses-not-from-this-fund',
  ),
  'total-expenses-not-from-this-fund': t(
    'ks-aggregation-target-total-expenses-not-from-this-fund',
  ),
} as const satisfies Record<
  z.infer<typeof ksAggregationTargetTypeEnum>,
  string
>;
